import React, { FC } from 'react';
import { ReverseVestingOnOption } from 'shared';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '../../../../components/combobox/index';
import { useLocale } from '../../../../hooks/index';
import { StringKey } from '../../../../lang/index';

/**
 * Same three options as plan vesting, but the anchor is labelled "Issuance date" — a Grant Date
 * means nothing on an issuance row.
 */
export const ReverseVestingOnTitle: Record<ReverseVestingOnOption, StringKey> = {
  [ReverseVestingOnOption.ISSUANCE_DATE]: 'ISSUANCE_DATE',
  [ReverseVestingOnOption.FIRST_DATE]: 'FIRST_DAY_OF_MONTH',
  [ReverseVestingOnOption.LAST_DAY]: 'LAST_DAY_OF_MONTH',
};

export type ReverseVestingOnComboboxProps = {
  disabled?: boolean;
  onSelect: (state: ReverseVestingOnOption) => void;
  value?: ReverseVestingOnOption;
  wrapperClassName?: string;
};

/**
 * Fully controlled, unlike the plan modal's VestingOnCombobox. These render inside a
 * `useFieldArray`, where removing a row lets React reuse the component instance — local
 * `useState` seeded from a default would then keep showing the removed row's selection.
 */
export const ReverseVestingOnCombobox: FC<ReverseVestingOnComboboxProps> = ({
  disabled,
  onSelect,
  value,
  wrapperClassName,
}) => {
  const { messagesLocale } = useLocale();

  return (
    <Combobox
      as="div"
      className={twMerge('relative w-[192px]', wrapperClassName)}
      disabled={disabled}
      onChange={(state: ReverseVestingOnOption) => onSelect(state)}
    >
      <div className="relative">
        {/* Disabled, it reads like the other frozen fields: no chevron and no underline, which
            `Input` already drops on its own but `ComboboxInput` does not. */}
        <ComboboxInput
          className="bg-gray-900"
          disabled={disabled}
          icon={disabled ? undefined : <ChevronDownIcon className="mt-2" />}
          onChange={() => ''}
          placeholder={<AppFormattedMessage id="VESTING" />}
          readOnly
          value={value ? messagesLocale[ReverseVestingOnTitle[value]] : ''}
          wrapperClassName={disabled && '!border-transparent'}
        />
        {/* The invisible overlay is what actually opens the list, so a disabled field must not
            render it — `disabled` on the headless Combobox alone would leave a dead click target. */}
        {!disabled && <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />}
      </div>
      <ComboboxOptions className="w-full">
        {Object.entries(ReverseVestingOnTitle).map(([option, titleKey]) => (
          <ComboboxOption key={option} value={option}>
            <span className="text-sm text-gray-700">
              <AppFormattedMessage id={titleKey} />
            </span>
          </ComboboxOption>
        ))}
      </ComboboxOptions>
    </Combobox>
  );
};
